import { useMemo, type ReactElement } from 'react';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import NativeSelect from '@mui/material/NativeSelect';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import WindowOutlinedIcon from '@mui/icons-material/WindowOutlined';
import AppsIcon from '@mui/icons-material/Apps';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';
import LibraryAddCheckOutlinedIcon from '@mui/icons-material/LibraryAddCheckOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import type { SpatialObjectKind, WorkspaceId } from '@/types';
import { useWorkspaceStore, selectCanUndo, selectCanRedo } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useEngine } from '@/app/EngineContext';
import { useWindowStore } from '@/store/windowStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { OBJECT_KINDS, objectType } from '@/core/scene/objectTypes';
import {
  addObject,
  deleteSelection,
  duplicateSelection,
  groupSelection,
  ungroupSelection,
} from '@/features/objects/objectActions';
import { openApplication } from '@/features/windows/windowManager';
import { asApplicationId, asWorkspaceId } from '@/types';
import { IconButton } from '@/components/ui';
import styles from './Chrome.module.css';

/**
 * Primary toolbar. Below the expanded breakpoint the selection actions become icon
 * buttons (tooltips carry the labels); on phones it moves to the bottom edge.
 */
export const Toolbar = () => {
  const { applications, scene } = useEngine();
  const breakpoint = useBreakpoint();
  const compact = breakpoint === 'compact';
  // Text labels need ~1100px next to the inspector; below that the actions become icons.
  const dense = useMediaQuery('(max-width: 1199px)') || breakpoint !== 'expanded';
  const canUndo = useWorkspaceStore(selectCanUndo);
  const canRedo = useWorkspaceStore(selectCanRedo);
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const activeId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const selectedCount = useSelectionStore((s) => s.selection.objectIds.length);
  const workspaceOptions = useMemo(
    () => (Object.keys(workspaces) as WorkspaceId[]).map((id) => ({ id, name: workspaces[id]?.workspace.name ?? id })),
    [workspaces],
  );

  const action = (
    label: string,
    shortcut: string,
    icon: ReactElement,
    testId: string,
    onClick: () => void,
    disabled = false,
  ) =>
    dense ? (
      <IconButton label={label} shortcut={shortcut} data-testid={testId} disabled={disabled} onClick={onClick}>
        {icon}
      </IconButton>
    ) : (
      <Button
        data-testid={testId}
        disabled={disabled}
        onClick={onClick}
        startIcon={icon}
        title={`${label} (${shortcut})`}
      >
        {label}
      </Button>
    );

  return (
    <Paper
      component="nav"
      className={compact ? styles.toolbarCompact : styles.toolbar}
      role="toolbar"
      aria-label="Toolbar"
      data-layout={breakpoint}
      onPointerDown={(e) => {
        e.stopPropagation();
      }}
    >
      {!compact && (
        <Typography variant="h1" sx={{ mx: 2, fontSize: 15, fontWeight: 600, letterSpacing: '0.01em' }}>
          VectorLoom
        </Typography>
      )}

      {compact ? (
        <IconButton label="Add note" data-testid="add-note" onClick={() => addObject('note', applications)}>
          <NoteAddOutlinedIcon fontSize="small" />
        </IconButton>
      ) : (
        <Button
          color="inherit"
          data-testid="add-note"
          startIcon={<NoteAddOutlinedIcon />}
          onClick={() => addObject('note', applications)}
        >
          Add note
        </Button>
      )}
      {compact ? (
        <IconButton
          label="Open Notes"
          data-testid="add-window"
          onClick={() => openApplication(asApplicationId('app.notes'), applications)}
        >
          <WindowOutlinedIcon fontSize="small" />
        </IconButton>
      ) : (
        <Button
          color="inherit"
          data-testid="add-window"
          startIcon={<WindowOutlinedIcon />}
          onClick={() => openApplication(asApplicationId('app.notes'), applications)}
        >
          Open Notes
        </Button>
      )}
      <IconButton
        label="Applications"
        shortcut="⌘K"
        data-testid="open-launcher"
        onClick={() => {
          useWindowStore.getState().setLauncherOpen(true);
        }}
      >
        <AppsIcon fontSize="small" />
      </IconButton>
      <NativeSelect
        value=""
        disableUnderline
        className={styles.select}
        inputProps={{ 'aria-label': 'Add object', 'data-testid': 'add-object' } as Record<string, unknown>}
        onChange={(e) => {
          if (e.target.value) addObject(e.target.value as SpatialObjectKind, applications);
        }}
      >
        <option value="">{compact ? '…' : 'Add…'}</option>
        {OBJECT_KINDS.map((kind) => (
          <option key={kind} value={kind}>
            {objectType(kind).label}
          </option>
        ))}
      </NativeSelect>

      <Divider orientation="vertical" flexItem />
      <IconButton
        label="Undo"
        shortcut="⌘Z"
        data-testid="undo"
        disabled={!canUndo}
        onClick={() => {
          useWorkspaceStore.getState().undo();
        }}
      >
        <UndoIcon fontSize="small" />
      </IconButton>
      <IconButton
        label="Redo"
        shortcut="⇧⌘Z"
        data-testid="redo"
        disabled={!canRedo}
        onClick={() => {
          useWorkspaceStore.getState().redo();
        }}
      >
        <RedoIcon fontSize="small" />
      </IconButton>

      {!compact && (
        <>
          <Divider orientation="vertical" flexItem />
          {action(
            'Duplicate',
            '⌘D',
            <ContentCopyIcon fontSize="small" />,
            'duplicate',
            duplicateSelection,
            !selectedCount,
          )}
          {action(
            'Group',
            '⌘G',
            <LibraryAddOutlinedIcon fontSize="small" />,
            'group',
            () => {
              groupSelection(scene);
            },
            !selectedCount,
          )}
          {action(
            'Ungroup',
            '⇧⌘G',
            <LibraryAddCheckOutlinedIcon fontSize="small" />,
            'ungroup',
            () => {
              ungroupSelection(scene);
            },
            !selectedCount,
          )}
          {action('Delete', '⌫', <DeleteOutlineIcon fontSize="small" />, 'delete', deleteSelection, !selectedCount)}
        </>
      )}

      <Divider orientation="vertical" flexItem />
      <NativeSelect
        value={activeId}
        disableUnderline
        className={styles.select}
        inputProps={{ 'aria-label': 'Workspace', 'data-testid': 'workspace-switcher' } as Record<string, unknown>}
        onChange={(e) => {
          useWorkspaceStore.getState().switchWorkspace(asWorkspaceId(e.target.value));
        }}
      >
        {workspaceOptions.map((ws) => (
          <option key={ws.id} value={ws.id}>
            {ws.name}
          </option>
        ))}
      </NativeSelect>
      <IconButton
        label="New workspace"
        data-testid="new-workspace"
        onClick={() => useWorkspaceStore.getState().createWorkspace(`Workspace ${workspaceOptions.length + 1}`)}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
};
